import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import ReportActionsMenu from './ReportActionsMenu';
import CustomPagination from '../../../components/customPagination';
import { formatDate, formatDateWithTimestamp } from '../../../utils/formatDate';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';

const getReportStatusLabel = (status, t) => {
  const statusMap = {
    processed: t.status_completed,
    failed: t.status_failed,
    processing: t.status_processing,
    queued: t.status_queued,
  };
  return statusMap[status] ?? status;
};

const ReportTable = ({
  reports,
  loading,
  totalReports,
  onPageChange,
  onPageSizeChange,
  showCreatedBy = false,
  useAuthenticatedDownload = false,
  downloadDefaultFileName,
  downloadInsufficientAccessMessage,
}) => {
  const { documentReports: dr } = useTranslatedMessages();
  const tableT = dr.table ?? {};

  const createdByColumn = {
    field: 'email',
    headerName: tableT.created_by ?? 'Created By',
    flex: 0.8,
    align: 'left',
    renderCell: (params) => params.row.email ?? '',
  };

  const columns = [
    {
      field: 'created_at',
      headerName: tableT.request_date ?? 'Request Date',
      flex: 0.7,
      align: 'left',
      renderCell: (params) => formatDateWithTimestamp(params.row.createdAt),
    },
    {
      field: 'period',
      headerName: tableT.period ?? 'Period',
      flex: 1,
      align: 'left',
      renderCell: (params) => {
        const start = formatDate(params.row?.createdAfter) || '';
        const end = formatDate(params.row?.createdBefore) || '';
        const to = tableT.period_to ?? 'to';
        return `${start} ${to} ${end}`;
      },
    },
    {
      field: 'status',
      headerName: tableT.status ?? 'Status',
      flex: 0.6,
      align: 'left',
      id: 'report-status',
      renderCell: (params) => getReportStatusLabel(params.row.status?.toLowerCase(), tableT),
    },
    ...(showCreatedBy ? [createdByColumn] : []),
    {
      field: 'actions',
      headerName: tableT.actions ?? 'Actions',
      flex: 0.5,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        const isProcessed = params.row.status?.toLowerCase() === 'processed';
        return (
          <ReportActionsMenu
            filePath={params.row.filePath}
            jobId={params.row.id}
            useAuthenticatedDownload={useAuthenticatedDownload}
            downloadDefaultFileName={downloadDefaultFileName}
            downloadInsufficientAccessMessage={downloadInsufficientAccessMessage}
            disabled={!isProcessed}
          />
        );
      },
    },
  ];

  return (
    <DataGrid
      columns={columns}
      rows={reports}
      getRowId={(row) => row.id}
      sx={{ mt: '1rem' }}
      disableRowSelectionOnClick={true}
      loading={loading}
      paginationMode="server"
      rowCount={totalReports}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 10,
          },
        },
      }}
      pageSizeOptions={[10, 25]}
      onPaginationModelChange={({ page, pageSize }) => {
        onPageChange(page);
        onPageSizeChange(pageSize);
      }}
      slots={{
        pagination: CustomPagination,
      }}
    />
  );
};

export default ReportTable;
